/**
 * #1184 — the read-only account record a click on old Kalshi activity opens.
 *
 * Each originating surface maps its OWN row into this shape with the row's own
 * conventions (its price basis, its fee evidence, its P&L privacy mask); the
 * dialog only lays it out. A fact the row does not carry is `null` and renders
 * as "Not in this record" — never 0, a current quote, a fee basis or a
 * settlement result. No P&L figure: the record is what was recorded, not a
 * new calculation. The label is NOT stored here: the dialog derives it from
 * `describeMarket` on each render, so late saved metadata repaints it in place.
 */
import type { KalshiPosition } from '@/types/kalshi';
import type { KalshiOrPolyTapeRow } from '@/lib/fills/recentFillTape';
import type { TradeCardVM } from '@/components/cards/TradeCard';
import { fmtCents, fmtUsd } from '@/components/cards/cardFormat';
import { maskPnl } from '@/lib/pnlMask';

export interface RecordFact {
  label: string;
  /** Null: the originating row carries no such evidence. */
  value: string | null;
}

export interface AccountRecordVM {
  /** The originating row's own key; the record closes when it stops resolving. */
  key: string;
  ticker: string;
  side: 'yes' | 'no';
  /** "BUY" / "SELL" / "HELD" / "SETTLED" … — the row's own action word. */
  actionLabel: string;
  facts: RecordFact[];
  /** The Game Detail href the row would have used, checked live by the dialog. */
  candidateHref: string | null;
}

/** Exact native quantity — the record never rounds a fractional count (the
 *  card grid's whole-number `fmtQty` is a display choice for scanning). */
export function exactQty(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  return n.toLocaleString('en-US', { maximumFractionDigits: 8 });
}

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});

/** Local wall time of a recorded execution/close. Null for a missing or
 *  non-positive epoch (rows use 0 for "time unknown"). */
export function recordTime(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  return TIME_FMT.format(new Date(ms));
}

const cents = (c: number | null | undefined) => (c != null && Number.isFinite(c) ? fmtCents(c) : null);
const usd = (d: number | null | undefined) => (d != null && Number.isFinite(d) ? fmtUsd(d) : null);

/** A Kalshi Recent Fills row: one execution. A fill carries no fee or settlement. */
export function recordFromTapeRow(row: KalshiOrPolyTapeRow, candidateHref: string | null): AccountRecordVM {
  return {
    key: row.key, ticker: row.ticker, side: row.side, actionLabel: row.action.toUpperCase(), candidateHref,
    facts: [
      { label: 'Quantity', value: exactQty(row.count) },
      { label: 'Fill price', value: cents(row.priceCents) },
      { label: 'Executed', value: recordTime(row.executedAtMs) },
      { label: 'Fee', value: null },
    ],
  };
}

/** A held Kalshi position (Position Tracker). Average entry is unavailable
 *  when the read flags its basis unknown; fees are the venue-billed figure,
 *  masked while the page hides P&L (the stricter of the Tracker's card/table). */
export function recordFromPosition(pos: KalshiPosition, key: string, hidePnl: boolean, candidateHref: string | null): AccountRecordVM {
  const lastFillMs = pos.lastFillAt ? Date.parse(pos.lastFillAt) : null;
  return {
    key, ticker: pos.ticker, side: pos.side, actionLabel: 'HELD', candidateHref,
    facts: [
      { label: 'Quantity held', value: exactQty(Math.abs(pos.position)) },
      { label: 'Average entry', value: pos.entryBasisUnknown ? null : cents(pos.averagePrice) },
      { label: 'Last fill', value: recordTime(lastFillMs) },
      { label: 'Fees paid', value: pos.feesPaidCents != null ? maskPnl(hidePnl, fmtUsd(pos.feesPaidCents / 100)) : null },
    ],
  };
}

/** A My Trades row, through the TradeCardVM both Trader and Audit tabs build.
 *  Fees follow the card's P&L privacy mask, exactly as the card shows them. */
export function recordFromTrade(vm: TradeCardVM, hidePnl: boolean, candidateHref: string | null): AccountRecordVM {
  const fee = usd(vm.feesDollars);
  return {
    key: vm.key, ticker: vm.ticker, side: vm.side, actionLabel: vm.action, candidateHref,
    facts: [
      { label: 'Quantity', value: exactQty(vm.qty) },
      { label: 'Average entry', value: cents(vm.entryCents) },
      { label: vm.action === 'SETTLED' ? 'Settlement value' : 'Average exit', value: cents(vm.exitCents) },
      { label: vm.action === 'OPEN' ? 'Opened' : 'Closed', value: recordTime(vm.closedAtMs) },
      { label: 'Fees', value: fee == null ? null : maskPnl(hidePnl, fee) },
      ...(vm.isRejected ? [{ label: 'Rejected', value: vm.rejectedReason ?? 'rejected' }] : []),
    ],
  };
}
